// @ts-check
// Gaveta de navegação (doc 07, seções 4 e 5): seletor do workspace com tema e saída, itens Buscar, Conversas,
// Painéis e Configurações, objetos do menu agrupados por uso (dia a dia, captação, secretaria), colapso para trilho de ícones
// e sobreposição abaixo de 768 px. A estrutura da escola (unidades, cursos, períodos) fica nas configurações.
import { html } from 'htm/preact';
import { useId, useRef, useState } from 'preact/hooks';
import { Link, locationState, navigate } from '../../app/router.js';
import { COLOR_SCHEMES, COLOR_SCHEME_ICONS, colorSchemeState, setColorScheme } from '../../app/theme.js';
import { hasCurrentPermissionFlag, isCoordinationFocusedState, navigationItemsState, objectsById, objectsByNameSingular, sessionState } from '../../data/metadata-store.js';
import { FavoritesNavigationSection } from './favorites-section.js';
import { EditableNavigationSectionItems, NavigationEditBar, navigationDraftState, startNavigationEdit } from './navigation-editor.js';
import { OnboardingChecklist } from './onboarding-checklist.js';
import { APP_VERSION } from '../../../../shared/app-version.js';
import { appEnvironmentState } from '../../lib/app-environment.js';
// seções do menu num lugar só, com Campanhas desde a 2.0.0 (spec-2.0.0 seção 4.1)
import { MAIN_NAVIGATION_SECTIONS } from '../../../../shared/navigation/navigation-sections.js';
import { isLinkItemActive, visibleNavigationSections } from '../../lib/navigation-visibility.js';
import { isSettingsLocation, schoolStructureItemOf } from '../../lib/settings-location.js';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { useIsMobile } from '../../ui/hooks.js';
import { openCommandMenu } from '../side-panel/side-panel-state.js';
import { isMobileNavigationOpenState, isNavigationDrawerExpandedState, toggleNavigationDrawer } from './navigation-state.js';
import { signOutAndRedirect } from '../auth/session.js';
import { listMyInstitutions, switchInstitution } from '../../data/workspace-settings.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatShortcut } from '../../lib/utils.js';
import { unreadConversationCountState, useUnreadConversationPolling } from '../conversations/conversations-state.js';
import { t } from '../../i18n/index.js';
import { ReleaseNotesBalloon } from '../release-notes/release-notes-balloon.js';
import { useReleaseNotesSurface } from '../release-notes/release-notes-state.js';
import { markReleaseNotesSeen } from '../../data/release-notes.js';
import { BrandLogo } from '../../ui/brand-logo.js';

/**
 * @param {{ label: string, icon: string, href?: string, onClick?: () => void, isActive?: boolean, hint?: string, count?: number, isExternal?: boolean }} props
 */
function NavigationItem({ label, icon, href, onClick, isActive = false, hint, count = 0, isExternal = false }) {
  const isCollapsed = !isNavigationDrawerExpandedState.value && !isMobileNavigationOpenState.value;
  const content = html`
    <span class="navigation-item-icon">
      <${Icon} name=${icon} size=${16} />
      ${count > 0 ? html`<span class="navigation-item-dot" aria-hidden="true"></span>` : null}
    </span>
    <span class="navigation-item-label">${label}</span>
    ${hint ? html`<span class="navigation-item-hint">${hint}</span>` : null}
    ${count > 0 ? html`<span class="navigation-item-count">${count > 99 ? '99+' : count}</span>` : null}
  `;
  const handleClick = () => {
    isMobileNavigationOpenState.value = false;
    onClick?.();
  };
  // link externo do menu personalizado abre em outra aba (doc 07, 4.7)
  if (href && isExternal) {
    return html`<a class="navigation-item" href=${href} target="_blank" rel="noopener noreferrer" title=${isCollapsed ? label : undefined} onClick=${handleClick}>
      ${content}<${Icon} name="IconArrowUpRight" size=${12} className="navigation-item-external" />
    </a>`;
  }
  if (href) {
    return html`<${Link}
      href=${href}
      className="navigation-item"
      data-active=${isActive ? 'true' : undefined}
      aria-current=${isActive ? 'page' : undefined}
      title=${isCollapsed ? label : undefined}
      onClick=${handleClick}
    >${content}<//>`;
  }
  return html`<button type="button" class="navigation-item" title=${isCollapsed ? label : undefined} onClick=${handleClick}>${content}</button>`;
}

/**
 * @param {{ id: string, title: string, children: any, rightAction?: any }} props
 */
function NavigationSection({ id, title, children, rightAction }) {
  const storageKey = `appcrm.navigationSection.${id}`;
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) !== 'false';
    } catch {
      return true;
    }
  });
  return html`<div class="navigation-section">
    <button
      type="button"
      class="navigation-section-title"
      aria-expanded=${isOpen ? 'true' : 'false'}
      onClick=${() => {
        const next = !isOpen;
        setIsOpen(next);
        try {
          window.localStorage.setItem(storageKey, String(next));
        } catch {
          // Sem localStorage o estado vale só nesta sessão.
        }
      }}
    >
      <span>${title}</span>
      <${Icon} name="IconChevronRight" size=${12} stroke=${2.5} className="navigation-section-chevron" />
    </button>
    ${rightAction ? html`<span class="navigation-section-action">${rightAction}</span>` : null}
    ${isOpen ? html`<div class="navigation-section-items">${children}</div>` : null}
  </div>`;
}

function WorkspaceDropdown() {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [subMenu, setSubMenu] = useState(/** @type {'default' | 'themes' | 'institutions'} */ ('default'));
  // instituições da pessoa (doc 12, seção 6.4), lidas ao abrir o menu
  const [institutions, setInstitutions] = useState(/** @type {import('../../data/workspace-settings.js').Institution[]} */ ([]));
  const [switchingSlug, setSwitchingSlug] = useState(/** @type {string | null} */ (null));
  const tenant = sessionState.value?.tenant;
  const displayName = tenant?.displayName ?? t('navigation.defaultWorkspaceName');
  const close = () => {
    setIsOpen(false);
    setSubMenu('default');
  };
  const toggle = () => {
    if (!isOpen) listMyInstitutions().then(setInstitutions).catch(() => setInstitutions([]));
    setIsOpen(!isOpen);
  };
  /** @param {string} slug */
  const switchTo = async (slug) => {
    setSwitchingSlug(slug);
    try {
      window.location.assign(await switchInstitution(slug));
    } catch (error) {
      setSwitchingSlug(null);
      showErrorSnackbar(error);
    }
  };
  return html`<div class="workspace-dropdown">
    <button ref=${anchorRef} type="button" class="workspace-button" data-testid="workspace-dropdown" onClick=${toggle}>
      <${Avatar} placeholder=${displayName} seed=${tenant?.id ?? displayName} size="lg" type="squared" avatarUrl=${tenant?.logoUrl ?? null} />
      <span class="workspace-button-label">${displayName}</span>
      <${Icon} name="IconChevronDown" size=${16} stroke=${1.6} className="workspace-button-chevron" />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} width=${220} matchAnchorTop=${true}>
      ${subMenu === 'institutions'
        ? html`
            <${DropdownMenuHeader} title=${t('workspaceSettings.switcher.title')} onBack=${() => setSubMenu('default')} />
            <${DropdownMenuItems} hasMaxHeight=${true}>
              ${institutions.map((institution) => html`<${MenuItem}
                key=${institution.slug}
                leftComponent=${html`<${Avatar} placeholder=${institution.displayName} seed=${institution.slug} size="md" type="squared" avatarUrl=${institution.logoUrl} />`}
                text=${institution.displayName || t('workspaceSettings.switcher.noName')}
                contextualText=${switchingSlug === institution.slug ? t('workspaceSettings.switcher.opening') : undefined}
                selected=${institution.isCurrent}
                disabled=${Boolean(switchingSlug)}
                onClick=${() => (institution.isCurrent ? close() : switchTo(institution.slug))}
              />`)}
            <//>
          `
        : subMenu === 'themes'
        ? html`
            <${DropdownMenuHeader} title=${t('navigation.theme')} onBack=${() => setSubMenu('default')} />
            <${DropdownMenuItems}>
              ${COLOR_SCHEMES.map(
                (scheme) => html`<${MenuItem}
                  key=${scheme}
                  icon=${COLOR_SCHEME_ICONS[scheme]}
                  text=${t(`colorSchemes.${scheme}`)}
                  selected=${colorSchemeState.value === scheme}
                  onClick=${() => setColorScheme(scheme)}
                />`,
              )}
            <//>
          `
        : html`
            <div class="dropdown-menu-header workspace-dropdown-header">
              <${Avatar} placeholder=${displayName} seed=${tenant?.id ?? displayName} size="lg" type="squared" avatarUrl=${tenant?.logoUrl ?? null} />
              <span class="dropdown-menu-header-title">${displayName}</span>
            </div>
            <${DropdownMenuItems}>
              ${institutions.length > 1
                ? html`<${MenuItem}
                    icon="IconSwitchHorizontal"
                    text=${t('workspaceSettings.switcher.title')}
                    contextualText=${String(institutions.length)}
                    hasSubMenu=${true}
                    onClick=${() => setSubMenu('institutions')}
                  />`
                : null}
              <${MenuItem}
                icon=${COLOR_SCHEME_ICONS[colorSchemeState.value]}
                text=${t('navigation.theme')}
                contextualText=${t(`colorSchemes.${colorSchemeState.value}`)}
                hasSubMenu=${true}
                onClick=${() => setSubMenu('themes')}
              />
              <${MenuItem}
                icon="IconPaint"
                text=${t('navigationMobile.editor.open')}
                onClick=${() => {
                  close();
                  startNavigationEdit('MEMBER');
                }}
              />
              <${MenuItem}
                icon="IconSettings"
                text=${t('navigation.settings')}
                onClick=${() => {
                  close();
                  isMobileNavigationOpenState.value = false;
                  navigate('/settings/profile');
                }}
              />
              <${DropdownMenuSeparator} />
              <${MenuItem} icon="IconLogout" text=${t('navigation.signOut')} onClick=${() => { close(); signOutAndRedirect(); }} />
            <//>
          `}
    <//>
  </div>`;
}

/** Último caminho fora das configurações, para o botão de voltar da gaveta de configurações. */
let lastAppLocation = '/';

function MainNavigationContent() {
  const location = locationState.value;
  const currentViewId = new URLSearchParams(location.search).get('viewId');
  const items = navigationItemsState.value;
  const recordMatch = /^\/object\/([\w-]+)\//.exec(location.pathname);
  const recordObjectId = recordMatch ? objectsByNameSingular.value.get(recordMatch[1])?.id : null;
  const viewNavigationIds = new Set(items.filter((item) => item.viewId).map((item) => item.viewId));

  // itens de tela própria (Agenda, Visitas, painéis, telas de Campanhas) ficam ativos também nas subpáginas
  const linkHrefs = new Set(items.filter((item) => !item.objectMetadataId).map((item) => item.href));
  const isItemActive = (/** @type {(typeof items)[number]} */ item) => {
    const [itemPath] = item.href.split('?');
    if (!item.objectMetadataId) return isLinkItemActive(item.href, location.pathname);
    if (recordObjectId) return !item.viewId && item.objectMetadataId === recordObjectId;
    if (location.pathname !== itemPath) return false;
    if (item.viewId) return currentViewId === item.viewId;
    return !currentViewId || !viewNavigationIds.has(currentViewId);
  };

  // Coordenação focada: Campanhas (quando o papel tem alguma flag delas) e Coordenação; seção sem item some
  const sections = visibleNavigationSections({ isCoordinationFocused: isCoordinationFocusedState.value, hasPermissionFlag: hasCurrentPermissionFlag });
  // modo de edição do menu (contrato 08 de navegação): itens com subir, descer, ocultar e adicionar por seção
  if (navigationDraftState.value) {
    return html`
      <${NavigationEditBar} />
      ${sections.map((section) => html`<div class="navigation-section" key=${section}>
        <div class="navigation-section-title navigation-section-title-static"><span>${t(`navigation.sections.${section}`)}</span></div>
        <${EditableNavigationSectionItems} section=${section} sections=${MAIN_NAVIGATION_SECTIONS} />
      </div>`)}
    `;
  }

  return html`
    <div class="navigation-section-items navigation-top-items">
      <${NavigationItem} label=${t('navigation.search')} icon="IconSearch" hint=${formatShortcut('K')} onClick=${openCommandMenu} />
      ${hasCurrentPermissionFlag('CONVERSATIONS')
        ? html`<${NavigationItem}
            label=${t('navigation.conversations')}
            icon="IconMessages"
            href="/conversations"
            isActive=${location.pathname === '/conversations' || location.pathname.startsWith('/conversations/')}
            count=${unreadConversationCountState.value}
          />`
        : null}
      ${hasCurrentPermissionFlag('DASHBOARDS')
        ? html`<${NavigationItem}
            label=${t('navigation.dashboards')}
            icon="IconLayoutDashboard"
            href="/dashboards"
            isActive=${location.pathname === '/dashboards' || (location.pathname.startsWith('/dashboards/') && !linkHrefs.has(location.pathname))}
          />`
        : null}
      <${NavigationItem} label=${t('navigation.settings')} icon="IconSettings" href="/settings/profile" />
    </div>
    <${FavoritesNavigationSection} NavigationSection=${NavigationSection} NavigationItem=${NavigationItem} />
    ${sections.map((section) => {
      // a estrutura da escola fica nas configurações; itens sem grupo conhecido (objetos da escola) vão para o dia a dia
      const sectionItems = items.filter((item) => item.section !== 'SCHOOL_STRUCTURE'
        && (MAIN_NAVIGATION_SECTIONS.includes(/** @type {any} */ (item.section)) ? item.section : 'MAIN') === section);
      if (sectionItems.length === 0) return null;
      return html`<${NavigationSection} key=${section} id=${section === 'MAIN' ? 'Workspace' : section} title=${t(`navigation.sections.${section}`)}>
        ${sectionItems.map((item) => html`<${NavigationItem}
          key=${item.id}
          label=${item.label}
          icon=${item.icon}
          href=${item.href}
          isExternal=${item.isExternal}
          isActive=${isItemActive(item)}
        />`)}
      <//>`;
    })}
  `;
}

/** Itens da Estrutura da escola mostrados nas configurações (a Coordenação com menu focado não os vê). */
const settingsSchoolStructureItems = () => (isCoordinationFocusedState.value ? [] : navigationItemsState.value.filter((item) => item.section === 'SCHOOL_STRUCTURE'));

function SettingsNavigationContent() {
  const pathname = locationState.value.pathname;
  const schoolStructureItems = settingsSchoolStructureItems();
  return html`
    <${NavigationSection} id="settings/User" title=${t('settings.sections.user')}>
      <${NavigationItem} label=${t('settings.profile.title')} icon="IconUserCircle" href="/settings/profile" isActive=${pathname === '/settings/profile' || pathname === '/settings'} />
      <${NavigationItem} label=${t('workspaceSettings.experience.title')} icon="IconColorSwatch" href="/settings/experience" isActive=${pathname.startsWith('/settings/experience')} />
      <${NavigationItem} label=${t('notifications.settingsTitle')} icon="IconBell" href="/settings/notifications" isActive=${pathname.startsWith('/settings/notifications')} />
      <${NavigationItem} label=${t('calendar.accounts.title')} icon="IconAt" href="/settings/accounts" isActive=${pathname.startsWith('/settings/accounts')} />
    <//>
    ${schoolStructureItems.length > 0
      ? html`<${NavigationSection} id="settings/SchoolStructure" title=${t('settings.sections.schoolStructure')}>
          ${schoolStructureItems.map((item) => html`<${NavigationItem} key=${item.id} label=${item.label} icon=${item.icon} href=${item.href}
            isActive=${schoolStructureItemOf(pathname, [item], objectsById.value) !== null} />`)}
        <//>`
      : null}
    <${NavigationSection} id="settings/Workspace" title=${t('settings.sections.workspace')}>
      ${hasCurrentPermissionFlag('WORKSPACE')
        ? html`<${NavigationItem} label=${t('workspaceSettings.general.title')} icon="IconSettings" href="/settings/general" isActive=${pathname.startsWith('/settings/general')} />`
        : null}
      ${hasCurrentPermissionFlag('SECURITY')
        ? html`<${NavigationItem} label=${t('workspaceSettings.security.title')} icon="IconKey" href="/settings/security" isActive=${pathname.startsWith('/settings/security')} />`
        : null}
      ${isCoordinationFocusedState.value
        ? null
        : html`<${NavigationItem} label=${t('settings.objects.title')} icon="IconHierarchy2" href="/settings/objects" isActive=${pathname.startsWith('/settings/objects')} />
          <${NavigationItem} label=${t('dataModel.funnel.title')} icon="IconLayoutKanban" href="/settings/funnel" isActive=${pathname.startsWith('/settings/funnel')} />
          <${NavigationItem} label=${t('settings.channels.title')} icon="IconPlugConnected" href="/settings/channels" isActive=${pathname.startsWith('/settings/channels')} />
          <${NavigationItem} label=${t('whatsappTemplates.title')} icon="IconBrandWhatsapp" href="/settings/whatsapp-templates" isActive=${pathname.startsWith('/settings/whatsapp-templates')} />`}
      ${hasCurrentPermissionFlag('WORKSPACE_MEMBERS') || hasCurrentPermissionFlag('ROLES')
        ? html`<${NavigationItem} label=${t('team.members.title')} icon="IconUsers" href="/settings/members" isActive=${pathname.startsWith('/settings/members')} />`
        : null}
      ${hasCurrentPermissionFlag('ROLES')
        ? html`<${NavigationItem} label=${t('team.roles.title')} icon="IconShieldLock" href="/settings/roles" isActive=${pathname.startsWith('/settings/roles')} />`
        : null}
      ${hasCurrentPermissionFlag('WORKFLOWS')
        ? html`<${NavigationItem} label=${t('automations.title')} icon="IconSettingsAutomation" href="/settings/automations" isActive=${pathname.startsWith('/settings/automations')} />`
        : null}
      ${hasCurrentPermissionFlag('WORKSPACE')
        ? html`<${NavigationItem} label=${t('leadCapture.forms.title')} icon="IconFileDescription" href="/settings/capture-forms" isActive=${pathname.startsWith('/settings/capture-forms')} />
          <${NavigationItem} label=${t('bookingPages.title')} icon="IconCalendarPlus" href="/settings/booking-pages" isActive=${pathname.startsWith('/settings/booking-pages')} />`
        : null}
      ${hasCurrentPermissionFlag('API_KEYS_AND_WEBHOOKS')
        ? html`<${NavigationItem} label=${t('leadCapture.developers.title')} icon="IconApi" href="/settings/developers" isActive=${pathname.startsWith('/settings/developers')} />`
        : null}
    <//>
    ${hasCurrentPermissionFlag('BILLING') || hasCurrentPermissionFlag('SECURITY')
      ? html`<${NavigationSection} id="settings/Operations" title=${t('operations.settingsSection')}>
          ${hasCurrentPermissionFlag('BILLING')
            ? html`<${NavigationItem} label=${t('operations.billing.title')} icon="IconReceipt" href="/settings/billing" isActive=${pathname.startsWith('/settings/billing')} />`
            : null}
          ${hasCurrentPermissionFlag('SECURITY')
            ? html`<${NavigationItem} label=${t('operations.privacy.title')} icon="IconShieldLock" href="/settings/privacy" isActive=${pathname.startsWith('/settings/privacy')} />
              <${NavigationItem} label=${t('operations.audit.title')} icon="IconHistory" href="/settings/audit-log" isActive=${pathname.startsWith('/settings/audit-log')} />`
            : null}
        <//>`
      : null}
    <${NavigationSection} id="settings/Other" title=${t('settings.sections.other')}>
      <${NavigationItem} label=${t('navigation.signOut')} icon="IconLogout" onClick=${signOutAndRedirect} />
    <//>
  `;
}

/**
 * Rodapé da gaveta (contrato 08-contrato-novidades.md, seção 5): a versão é o link para a página Novidades, com o chip
 * "Novo" enquanto há anúncio pendente, e é a âncora do balão. A div é a âncora porque o Link não repassa ref.
 * @param {{ isMobile: boolean }} props
 */
function NavigationDrawerVersion({ isMobile }) {
  const footerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const versionLinkRef = useRef(/** @type {HTMLElement | null} */ (null));
  const idSuffix = useId();
  const titleId = `release-notes-balloon-title-${idSuffix}`;
  const listId = `release-notes-balloon-list-${idSuffix}`;
  const { announcement, surface } = useReleaseNotesSurface();
  const isBalloonOpen = surface === 'balloon' && Boolean(announcement);
  return html`<div
    class="navigation-drawer-version"
    ref=${(/** @type {HTMLDivElement | null} */ element) => {
      footerRef.current = element;
      versionLinkRef.current = element?.querySelector('.navigation-drawer-version-link') ?? null;
    }}
  >
    <${Link}
      href="/release-notes"
      className="navigation-drawer-version-link"
      aria-label=${t(announcement ? 'releaseNotes.versionLink.labelWithNews' : 'releaseNotes.versionLink.label', { version: APP_VERSION })}
      aria-describedby=${isBalloonOpen ? titleId : undefined}
      title=${t('releaseNotes.versionLink.title')}
      data-testid="navigation-drawer-version-link"
      onClick=${() => {
        if (isMobile) isMobileNavigationOpenState.value = false;
        if (announcement) markReleaseNotesSeen();
      }}
    >
      <${BrandLogo} height=${16} />
      <span class="navigation-drawer-version-number">${t('navigation.versionNumber', { version: APP_VERSION })}</span>
      ${announcement ? html`<span class="release-notes-chip release-notes-new-badge" aria-hidden="true">${t('releaseNotes.versionLink.newBadge')}</span>` : null}
    <//>
    ${appEnvironmentState.value !== 'production' ? html`<span class="app-environment-badge" data-environment=${appEnvironmentState.value}>${t(`navigation.environment.${appEnvironmentState.value}`)}</span>` : null}
    ${isBalloonOpen && announcement
      ? html`<${ReleaseNotesBalloon} anchorRef=${footerRef} versionLinkRef=${versionLinkRef} announcement=${announcement} titleId=${titleId} listId=${listId} />`
      : null}
  </div>`;
}

export function NavigationDrawer() {
  const isMobile = useIsMobile();
  useUnreadConversationPolling(hasCurrentPermissionFlag('CONVERSATIONS'));
  const pathname = locationState.value.pathname;
  // a lista e o registro de Unidades, Cursos e Períodos letivos ficam no menu de Configurações, de onde foram abertos
  const isSettings = isSettingsLocation(pathname, settingsSchoolStructureItems(), objectsById.value);
  if (!isSettings) lastAppLocation = `${pathname}${locationState.value.search}`;
  // A gaveta de configurações nunca vira trilho de ícones (doc 07, 4.2).
  const isExpanded = isMobile || isSettings || isNavigationDrawerExpandedState.value;
  const isMobileOpen = isMobileNavigationOpenState.value;
  // no modo de edição a gaveta se alarga para caber as ações de cada item (doc 07, 4.8)
  const isEditingMenu = !isSettings && Boolean(navigationDraftState.value);

  return html`
    ${isMobile && isMobileOpen ? html`<div class="navigation-backdrop" onClick=${() => (isMobileNavigationOpenState.value = false)}></div>` : null}
    <aside
      class="navigation-drawer"
      data-expanded=${isExpanded || isEditingMenu ? 'true' : 'false'}
      data-editing=${isEditingMenu ? 'true' : undefined}
      data-mobile-open=${isMobileOpen ? 'true' : undefined}
    >
      <div class="navigation-drawer-inner">
        <div class="navigation-drawer-header">
          ${isSettings
            ? html`<button type="button" class="navigation-back-button" onClick=${() => navigate(lastAppLocation)}>
                <${Icon} name="IconX" size=${16} stroke=${1.6} />
                <span>${t('navigation.settings')}</span>
              </button>`
            : html`<${WorkspaceDropdown} />`}
          <div class="navigation-drawer-header-actions">
            ${isMobile
              ? html`<${LightIconButton} icon="IconX" ariaLabel=${t('common.close')} onClick=${() => (isMobileNavigationOpenState.value = false)} />`
              : !isSettings
                ? html`<${LightIconButton} icon="IconLayoutSidebarLeftCollapse" ariaLabel=${isExpanded ? t('navigation.collapse') : t('navigation.expand')} onClick=${toggleNavigationDrawer} data-navigation-drawer-toggle="true" />`
                : null}
          </div>
        </div>
        <nav class="navigation-drawer-scroll">
          ${isSettings ? html`<${SettingsNavigationContent} />` : html`<${MainNavigationContent} />`}
        </nav>
        ${!isSettings && isExpanded && !navigationDraftState.value ? html`<${OnboardingChecklist} />` : null}
        ${isExpanded || isMobile ? html`<${NavigationDrawerVersion} isMobile=${isMobile} />` : null}
      </div>
    </aside>
  `;
}
